<script setup lang="ts">
import { Drawer, DrawerContent } from '../ui/drawer';
import { onMounted, onUnmounted, reactive } from 'vue';
import { Progress } from '../ui/progress';
import { DialogHeader, DialogTitle } from '../ui/dialog';
import { eventEmitter, type ShowScreenCapturePreviewPayload } from '@/lib/eventEmitter.ts';

interface ComponentState {
    screenCaptureData: string | undefined;
    isOpen: boolean;
    progressBarValue: number;
    screenCaptureCount: number;
}

const state: ComponentState = reactive({
    screenCaptureData: undefined,
    isOpen: false,
    progressBarValue: 100,
    screenCaptureCount: 0,
});

const openDrawer = (payload: ShowScreenCapturePreviewPayload) => {
    state.screenCaptureData = payload.screenCaptureData;
    state.screenCaptureCount = payload.screenCaptureCount;
    state.isOpen = true;
    state.progressBarValue = 100;

    const progressBarInterval = setInterval(() => {
        state.progressBarValue -= 2.5;
        if (state.progressBarValue <= 0) {
            state.isOpen = false;
            clearInterval(progressBarInterval);
        }
    }, 2000 / 40); // 2 seconds (20ms interval)
};

onMounted(() => {
    eventEmitter.addListener('showScreenCapturePreview', openDrawer);
});

onUnmounted(() => {
    eventEmitter.removeListener('showScreenCapturePreview', openDrawer);
});
</script>

<template>
    <Drawer :open="state.isOpen">
        <DrawerContent class="outline-none">
            <DialogHeader>
                <DialogTitle :hidden="true">
                    <span v-if="state.screenCaptureData">Processing your input:</span>
                    <span v-else>Processing your input...</span>
                </DialogTitle>
            </DialogHeader>
            <div class="my-4 flex flex-col items-center justify-center">
                <span v-if="state.screenCaptureData">
                    Processing screenshot {{ state.screenCaptureCount }}:
                </span>
                <span v-else>Processing your input...</span>
                <span v-if="state.screenCaptureCount > 0" class="text-sm text-muted-foreground">
                    {{ state.screenCaptureCount }}
                    {{ state.screenCaptureCount === 1 ? 'image' : 'images' }} attached
                </span>
                <img
                    v-if="state.screenCaptureData"
                    :src="state.screenCaptureData"
                    alt="Input preview"
                    class="my-4 max-w-[340px] rounded"
                />
            </div>
            <Progress :model-value="state.progressBarValue" class="h rounded-none opacity-50" />
        </DrawerContent>
    </Drawer>
</template>
